import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import {
  openOnboarding,
  closeSetupSnackbar,
} from "store/slice/globalControl/globalControlSlice";
import { getSession } from "services/service";

const SNACKBAR_SESSION_KEY = "globalSetupSnackbarShown";

const GlobalSnackbar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { Authorization } = getSession();

  const [open, setOpen] = useState(false);

  const guideActive = useSelector((state) => state.ui.guide.active);
  const {
    placesList = [],
    loading,
    error,
  } = useSelector((state) => state.places || {}, shallowEqual);

  const user = useSelector((state) => state.users?.meInfo);
  const setupSnackbarTrigger = useSelector(
    (state) => state.ui.setupSnackbar.open,
  );

  const role = user?.roleTitle;

  const isAllowedRole = ["Brand Admin", "Super Admin"].includes(role);

  // ✅ Role-based step keys
  const BRAND_ADMIN_KEYS = [
    "placeSettings",
    "pricingTierSettings",
    "ratesCreate",
    "ratesAssigned",
  ];

  const SUPER_ADMIN_KEYS = [
    "placeSettings",
    "pricingTierSettings",
    "generalSettings",
  ];

  const stepKeys =
    role === "Super Admin"
      ? SUPER_ADMIN_KEYS
      : role === "Brand Admin"
        ? BRAND_ADMIN_KEYS
        : [];

  // ✅ Role-based snackbar content
  const snackbarContentByRole = {
    "Super Admin": {
      title: "Complete Place Setup",
      subText:
        "Finish place, pricing, and system settings to activate the place.",
      actionLabel: "Finish Setup →",
    },
    "Brand Admin": {
      title: "Complete Place Setup",
      subText:
        "Finish pricing and rate configuration to enable enforcement and automation.",
      actionLabel: "Complete Setup →",
    },
  };

  const currentContent = snackbarContentByRole[role] || {
    title: "Complete Your Setup",
    subText: "Finish required setup steps to continue.",
    actionLabel: "Complete Setup →",
  };

  // ✅ Public routes block
  const isPublicRoute =
    location.pathname === "/" ||
    location.pathname.startsWith("/public") ||
    location.pathname.startsWith("/payment") ||
    location.pathname.startsWith("/parking") ||
    location.pathname.startsWith("/receipt") ||
    location.pathname.startsWith("/sub-") ||
    location.pathname.startsWith("/test") ||
    location.pathname.startsWith("/daily-") ||
    location.pathname.startsWith("/weekly-") ||
    location.pathname.startsWith("/monthly-") ||
    location.pathname.startsWith("/parker");

const shouldBlockSnackbar =
  !isAllowedRole ||
  isPublicRoute ||
  guideActive;
  
  // ✅ Reset session on login change
  useEffect(() => {
    if (Authorization) {
      sessionStorage.removeItem(SNACKBAR_SESSION_KEY);
    }
  }, [Authorization]);

  // ✅ Role-based incomplete logic (FIXED)
  const incompletePlaces = useMemo(() => {
    if (!Array.isArray(placesList) || !placesList.length) return [];

    return placesList.filter((place) => {
      const steps = place?.setup?.steps || {};
      const skipped = place?.setup?.skipped;

      if (skipped?.isPermanent) return false;

      const relevantSteps = stepKeys.map((key) => steps[key]);

      return relevantSteps.some((step) => step === false || step === undefined);
    });
  }, [placesList, stepKeys]);

  // ✅ Auto show snackbar
  useEffect(() => {
    if (shouldBlockSnackbar) return;

    const alreadyShown = sessionStorage.getItem(SNACKBAR_SESSION_KEY);

    if (
      Authorization &&
      !loading &&
      !error &&
      incompletePlaces.length > 0 &&
      !alreadyShown
    ) {
      sessionStorage.setItem(SNACKBAR_SESSION_KEY, "true");
      setOpen(true);
    }
  }, [
    Authorization,
    loading,
    error,
    incompletePlaces.length,
    shouldBlockSnackbar,
  ]);

  // ✅ Manual trigger
  useEffect(() => {
    if (shouldBlockSnackbar) return;

    if (setupSnackbarTrigger && incompletePlaces.length > 0) {
      setOpen(true);
      dispatch(closeSetupSnackbar());
    }
  }, [
    setupSnackbarTrigger,
    incompletePlaces.length,
    dispatch,
    shouldBlockSnackbar,
  ]);

  const handleClose = () => setOpen(false);

  const handleOpenChecklist = () => {
    const firstPlace = incompletePlaces[0];
    if (firstPlace?._id) {
      dispatch(openOnboarding(firstPlace._id));
    }
    setOpen(false);
  };

  if (shouldBlockSnackbar || !open || !incompletePlaces.length) return null;

  return (
    <MDSnackbar
      open={open}
      close={handleClose}
      icon="notifications"
      title={currentContent.title}
      dateTime="Action Required"
      color="success"
      autoHideDuration={null} // ✅ No auto close
      content={
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* MESSAGE */}
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            You have <b style={{ fontSize: 15 }}>{incompletePlaces.length}</b>{" "}
            place{incompletePlaces.length > 1 ? "s" : ""} with incomplete setup.
          </div>

          {/* ROLE BASED SUBTEXT */}
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            {currentContent.subText}
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 4,
            }}
          >
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              onClick={handleClose}
            >
              Dismiss
            </MDButton>

            <MDButton
              size="small"
              color="dark"
              variant="contained"
              onClick={handleOpenChecklist}
              sx={{ px: 2, fontWeight: "bold" }}
            >
              {currentContent.actionLabel}
            </MDButton>
          </div>
        </div>
      }
    />
  );
};

export default GlobalSnackbar;
